/**
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: valeriaplutino
 * Created: 8/12/21
 */

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import ExportWorkplan, { ExportWorkplanRef } from '@/components/export/ExportWorkplan';
import FocusHeader from '@/components/FocusHeader/FocusHeader';
import Icons from '@/components/icons/Icons';
import { Action, ListTable } from '@/components/pages/dashboard/tiles/bottomTile/bottomTileTables/ListTable';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';
import { PaginationToolbar, TableSkeletonLoading } from '@/components/Table';
import { Tile } from '@/components/Tile';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import {
    BulkActionResponse,
    mapWorkplanStageToString,
    mapWorkplanStateToString,
    UpdateMultipleWorkplansDto,
    Workplan,
    WorkplanStage,
    WorkplanState,
} from '@/wpt-lib/interfaces/Workplan';
import usePageTitle from '@/utils/hooks/usePageTitle';
import { defaultTableParams } from '@/utils/tableUtils';
import { useGetRequest, usePostRequest } from '@/utils/useStatefulRequests';
import { Box, Flex, Grid, useToast } from '@chakra-ui/react';
import * as React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useNavigateOptions from '@/components/contexts/NaviateContext/useNavigateOptions';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import config from '@/config';
import { formatFirstName } from '@/wpt-lib/utils/formatName';
import { Column, SupervisionToolbar } from '@/components/pages/Admin/SupervisionToolbar';
import { useSearchParams } from 'react-router-dom';
import { Dialog } from '@/components/Dialog';
import { EntityChange } from '@/wpt-lib/interfaces/EntityChange';
import BulkActionMessage from '@/components/BulkActions/BulkActionMessage';
import SupervisorProgressOptions from '@/components/pages/dashboard/tiles/ButtonTile/SupervisorProgressOptions';

type QueryOptions = {
    sort: string;
    order: string;
    assignState?: string;
    year?: string;
    probation?: string;
    state?: string;
    stage?: string;
};

const columns = [
    {
        name: 'ID',
        field: 'staffId',
        getData: (item: Workplan) => item.assignment?.user?.staffId,
        sort: false,
    },
    {
        name: 'Preferred Name',
        field: 'preferredName',
        getData: (item: Workplan) => formatFirstName(item.assignment?.user),
    },
    {
        name: 'Last Name',
        field: 'lastName',
        getData: (item: Workplan) => item.assignment?.user?.lastName,
    },
    {
        name: 'Assignment Role',
        field: 'assignment',
        getData: (item: Workplan) => item.assignment?.name,
    },
    {
        name: 'WP Year',
        field: 'year',
        getData: (item: Workplan) => item.year,
    },
    {
        name: 'WP Type',
        field: 'probation',
        getData: (item: Workplan) => (item.currentVersion.probationPlan ? 'Probation Plan' : 'Annual Workplan'),
        sort: false,
    },
    {
        name: 'WP Status',
        field: 'state',
        getData: (item: Workplan) => mapWorkplanStateToString[item.currentVersion.state],
        highlight: () => true,
    },
    {
        name: 'WP Stage',
        field: 'stage',
        getData: (item: Workplan) => mapWorkplanStageToString[item.currentVersion.stage],
        highlight: () => true,
    },
    {
        name: 'WP Version',
        field: 'version',
        getData: (item: Workplan) => item.currentVersion.version,
        sort: false,
    },
];

export interface WorkplanYearRangeInterface {
    maxYear: number;
    minYear: number;
}

export const SubmittedWorkplans = () => {
    usePageTitle('Submitted Workplans');

    const toast = useToast();
    const { currentAssignment } = useAssignment();
    const { navigatePath } = useNavigateOptions();

    const currentYear = '' + new Date().getFullYear();
    const [pageSize, setPageSize] = useState(defaultTableParams.pageSize);
    const [page, setPage] = useState(defaultTableParams.page);
    const [pages, setPages] = useState(0);
    const [bulkApprovePopup, setBulkApprovePopup] = useState(false);
    const [changeTo, setChangeTo] = useState<[WorkplanState, WorkplanStage?] | undefined>(undefined);
    const [selectedWorkplans, setSelectedWorkplans] = useState<Workplan[]>([]);

    const [focusPage, setFocusPage] = useState(false);

    const [search, setSearch] = useSearchParams();

    const [query, setQuery] = useState<QueryOptions>({
        sort: search.get('sort') ?? 'preferredName',
        order: search.get('order') ?? 'ASC',
        assignState: search.get('assignState') ?? 'ACTIVE',
        probation: search.get('probation') ?? 'All',
        year: search.get('year') ?? 'All',
        state: search.get('state') ?? 'All',
        stage: search.get('stage') ?? 'All',
    });

    const setQueryParam = <Param extends keyof QueryOptions>(param: Param, value: QueryOptions[Param]) => {
        setQueryParams({ [param]: value });
    };

    const setQueryParams = (update: Partial<QueryOptions>) =>
        setQuery((query) => {
            /* Deferring to not trigger render during state update. */
            setTimeout(() => {
                const params = Object.fromEntries(
                    Object.entries({
                        ...query,
                        ...update,
                        tab: 'submitted',
                    }).filter(([, v]) => v && v !== 'All'),
                );
                setSearch(params);
            });

            return { ...query, ...update };
        });

    const {
        makeRequest: makeYearsRequest,
        responseData: yearsResponse,
        error: yearsLoadingError,
    } = useGetRequest<{ maxYear: number; minYear: number }[]>();

    const getWorkplanYears = async () => {
        try {
            await makeYearsRequest('workplan/workplanYears');
        } catch {
            // handled by hook
        }
    };

    const sortColumns: Column[] = useMemo(
        () => [
            {
                label: 'Sort by',
                optionList: [
                    { name: 'Preferred Name', id: 'preferredName' },
                    { name: 'Last Name', id: 'lastName' },
                    { name: 'Assignment Role', id: 'assignment' },
                    { name: 'WP Year', id: 'year' },
                    { name: 'WP Status', id: 'state' },
                ],
                noAll: true,
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('sort', e.target.value),
                selectedValue: query.sort,
            },
            {
                label: 'Order',
                optionList: [
                    { name: 'Ascending', id: 'ASC' },
                    { name: 'Descending', id: 'DESC' },
                ],
                noAll: true,
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('order', e.target.value),
                selectedValue: query.order,
            },
        ],
        [query],
    );

    const filterColumns = useMemo(
        (): (Column & { admin?: boolean })[] => [
            {
                label: 'WP Year',
                optionList:
                    yearsResponse && yearsResponse?.length > 0
                        ? new Array(yearsResponse[0].maxYear - yearsResponse[0].minYear + 1).fill(1).map((_, i) => ({
                              name: '' + (yearsResponse[0].minYear + i),
                              id: '' + (yearsResponse[0].minYear + i),
                          }))
                        : [{ name: currentYear, id: currentYear }],
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('year', e.target.value),
                selectedValue: query.year,
            },
            {
                label: 'WP Type',
                optionList: [
                    { name: 'Probation Plan', id: 'Yes' },
                    { name: 'Annual Workplan', id: 'No' },
                ],
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('probation', e.target.value),
                selectedValue: query.probation,
            },
            {
                label: 'WP Status',
                optionList: [
                    { name: 'Submitted', id: WorkplanState.SUBMITTED },
                    { name: 'Review', id: WorkplanState.REVIEW },
                    { name: 'Moderation', id: WorkplanState.MODERATION },
                ],
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('state', e.target.value),
                selectedValue: query.state,
            },
            {
                label: 'WP Stage',
                optionList: [
                    { name: 'Planning', id: WorkplanStage.PLANNING },
                    { name: 'Part Year', id: WorkplanStage.PART_YEAR },
                    { name: 'End Year', id: WorkplanStage.END_YEAR },
                ],
                onChange: (e: React.ChangeEvent<HTMLSelectElement>) => setQueryParam('stage', e.target.value),
                selectedValue: query.stage,
            },
        ],
        [query, yearsResponse],
    );

    const {
        makeRequest: makeGetRequest,
        responseData: listResponse,
        loading: isGettingWorkplans,
        error: workplansLoadingError,
    } = useGetRequest<ListResponse<Workplan & EntityChange>>();

    const {
        makeRequest: makePostRequest,
        responseData: updatedResponse,
        loading: isUpdatingWorkplans,
        error: updatingError,
    } = usePostRequest<BulkActionResponse, UpdateMultipleWorkplansDto[]>();

    const { makeRequest: refreshSupervises } = useGetRequest<void>();

    useAsyncEffect(async () => {
        if (!currentAssignment) return;

        if (config.featureFlags.doRefreshSupervises) {
            try {
                await refreshSupervises(`users/supervising/${currentAssignment.id}`);
            } catch (e) {
                console.error('Error refreshing supervised users, ', e);
            }
        }
        await getSubmittedWorkplans();
    }, [currentAssignment]);

    useEffect(() => {
        getSubmittedWorkplans();
    }, [pageSize, page, pages, query]);

    useLayoutEffect(() => {
        getWorkplanYears();
    }, []);

    const bulkActionWorkplans = async () => {
        if (!changeTo) return;
        try {
            const resp = await makePostRequest(
                `workplan/bulk`,
                [...selectedWorkplans.map((w) => ({ id: w.id, update: { state: changeTo[0], stage: changeTo[1] } }))],
                {
                    params: {
                        assignment: currentAssignment?.id,
                    },
                },
            );
            setBulkApprovePopup(false);
            setChangeTo(undefined);
            setSelectedWorkplans([]);

            if (resp) {
                if (resp.data.updatedWorkplans.length > 0) {
                    toast({
                        title: 'Workplans Approved',
                        description: `${resp.data.updatedWorkplans.length} workplan${
                            resp.data.updatedWorkplans.length !== 1 ? 's' : ''
                        } Approved successfully.`,
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                    });
                }

                if (resp.data.rejectedWorkplans.length > 0) {
                    toast({
                        title: 'Workplans not Approved',
                        description: `${resp.data.rejectedWorkplans.length} workplan${
                            resp.data.rejectedWorkplans.length !== 1 ? 's' : ''
                        } could not be approved`,
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            }

            getSubmittedWorkplans();
        } catch {
            //handled by hook
        }
    };

    const getSubmittedWorkplans = async () => {
        try {
            const params = {
                page: page,
                pageSize: pageSize,
                ...query,
                orderByColumn: query.sort,
                orderByOrientation: query.order,
            };
            const res = await makeGetRequest(`workplan/submittedWorkplans`, {
                params,
            });
            setPages(res.data.info.pages);
            setPage(res.data.info.page);
        } catch {
            //handled by hook
        }
    };

    const exportRef = useRef<ExportWorkplanRef | null>(null);

    const getActions = (workplan: Workplan): Action<Workplan>[] => {
        const firstName = formatFirstName(workplan.assignment?.user);
        const lastName = workplan.assignment?.user?.lastName;
        const assignmentRole = workplan.assignment?.name;

        return [
            {
                id: 'select',
                label: 'Select',
                ariaLabel: `Select ${firstName} ${lastName} ${assignmentRole} workplan`,
                onSelectEvent: (workplans: Workplan[]) => {
                    setSelectedWorkplans([...workplans]);
                },
            },
            {
                id: 'view',
                event: (workplan: Workplan) => {
                    navigatePath('workplan', workplan.id, workplan.currentVersion.version);
                },
                label: 'View',
                ariaLabel: `View ${firstName} ${lastName} ${assignmentRole} workplan`,
                icon: <Icons.EyeIcon />,
            },
            {
                id: 'export',
                event: (workplan: Workplan) => {
                    if (currentAssignment)
                        exportRef.current?.export(workplan, workplan.currentVersion.version, currentAssignment);
                },
                label: 'Export',
                ariaLabel: `Export ${firstName} ${lastName} ${assignmentRole} workplan`,
                icon: <Icons.DownloadIcon />,
            },
        ];
    };

    if (isGettingWorkplans) {
        return (
            <Box w={'full'} p={4}>
                <Box bg={'offWhite'} p={4} rounded={'md'}>
                    <TableSkeletonLoading noOfCols={columns.length} noOfRows={10} />
                </Box>
            </Box>
        );
    }

    if (workplansLoadingError) {
        return (
            <Flex justifyContent="center">
                <SimpleAlert title="Error" variant="subtle">
                    Could not load list of submitted workplans
                </SimpleAlert>
            </Flex>
        );
    }

    return (
        <>
            {yearsLoadingError && (
                <SimpleAlert variant={'subtle'}>Could not load workplan years, please try again later</SimpleAlert>
            )}
            {updatingError && (
                <SimpleAlert variant="left-accent">
                    {`Couldn't complete the bulk action, please try again.`}
                </SimpleAlert>
            )}
            <Grid
                maxH={'auto'}
                w={'100%'}
                templateColumns={{
                    sm: 'repeat(1, 1fr)',
                    lg: 'repeat(2, 1fr)',
                }}
                gap={6}
                pt={3}
            >
                <Tile
                    overflowX={'auto'}
                    colspan={2}
                    heading={
                        <FocusHeader size={'small'} textColor={'blue.500'}>
                            Submitted Workplans
                        </FocusHeader>
                    }
                >
                    <Flex alignItems="center" width="full" flexWrap="wrap" gap={5}>
                        <SupervisionToolbar
                            label={`Total: ${listResponse && !isGettingWorkplans ? listResponse.info.total : '-'}`}
                            columns={[...sortColumns, ...filterColumns]}
                            textProps={{ fontWeight: 'normal' }}
                            showApproveButton={true}
                            buttonProps={{
                                chakraProps: {
                                    onClick: () => setBulkApprovePopup(true),
                                    colorScheme: 'blue',
                                    textAlign: 'center',
                                    isDisabled: !(
                                        selectedWorkplans.length > 0 &&
                                        selectedWorkplans.some((w) =>
                                            [
                                                WorkplanState.SUBMITTED,
                                                WorkplanState.REVIEW,
                                                WorkplanState.MODERATION,
                                            ].includes(w.currentVersion.state),
                                        )
                                    ),
                                    'aria-label': 'Bulk Approve',
                                },
                                label: 'Bulk Approve',
                            }}
                        />
                    </Flex>
                    <ListTable
                        tableCaption={`List of submitted workplans (page ${page + 1} of ${pages === 0 ? 1 : pages})`}
                        getActions={getActions}
                        columns={columns}
                        data={listResponse && listResponse.items}
                        errorRows={updatedResponse?.rejectedWorkplans.reduce((acc, rejected) => {
                            acc[`${rejected.id}`] = rejected.reason;
                            return acc;
                        }, {})}
                        idKey="id"
                        message={updatedResponse ? <BulkActionMessage updatedResponse={updatedResponse} /> : undefined}
                    />
                    <PaginationToolbar
                        setPageSize={setPageSize}
                        setNewPage={setPage}
                        pageSize={pageSize}
                        pages={pages}
                        page={page}
                        focusPage={focusPage}
                        setFocusPage={setFocusPage}
                    />
                </Tile>
            </Grid>
            <ExportWorkplan ref={exportRef} />
            <Dialog
                dialogHeader="Bulk Approve Workplans"
                dialogBody="Please choose the stage of the workplan which you would like to move to:"
                isOpen={bulkApprovePopup}
                isLoading={isUpdatingWorkplans}
                onClose={() => setBulkApprovePopup(false)}
                errorMsg={(updatingError as Error)?.message}
                dialogAction={() => bulkActionWorkplans()}
                confirmBtnProps={{ isDisabled: !changeTo }}
            >
                <SupervisorProgressOptions onSelect={setChangeTo} />
            </Dialog>
        </>
    );
};
